import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  ElementRef,
  inject,
  Injector,
  viewChild,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { NgProgressbar } from 'ngx-progressbar-next';
import { NgProgressRouter } from 'ngx-progressbar-next/router';
import { filter, take } from 'rxjs';

import { LocalStorageItemManager } from '@cosmos/data-access-storage';
import { IDLE$ } from '@cosmos/tick-scheduler';
import { HotToastLoader } from '@cosmos/ui-notification-toast';
import { injectDestroyRef, injectWindow } from '@cosmos/util-common/di';
import { clearSiteData } from '@cosmos/util-common/site-data-reset';
import {
  DataBusStateActionMessage,
  IframeDataBus,
  initStoreDispatcher,
} from '@cosmos/util-data-bus';
import { CustomerPortalAuthQueries } from '@customer-portal/auth/data-access-auth';
import {
  CustomerPortalVisitNotificationsService,
  injectSkipVisitTracker,
} from '@customer-portal/common/data-access-visit-notification';
import {
  CustomerPortalLayoutComponent,
  listenToResizeAndNotifyHostWindow,
} from '@customer-portal/common/ui-layout';
import { LookupsActions } from '@esp/lookup/data-access-lookup/actions';
import { CustomerPortalIframeEncoreStateActions } from '@esp/presentations/types-customer-portal-iframe-actions';

@Component({
  selector: 'customer-portal-app-root',
  templateUrl: './app-root.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgProgressbar, NgProgressRouter, CustomerPortalLayoutComponent],
  host: {
    // custom tag must have explicit display block to avoid adding one extra page in print preview
    class: 'block',
  },
})
export class AppRootComponent implements OnInit {
  private readonly _document = inject(DOCUMENT);
  private readonly _store = inject(Store);
  private readonly _injector = inject(Injector);
  private readonly _destroyRef = injectDestroyRef();
  private readonly _dataBus = new IframeDataBus({
    currentWindow: injectWindow(),
  });

  readonly layout = viewChild.required<string, ElementRef<HTMLElement>>(
    'layout',
    { read: ElementRef }
  );

  constructor() {
    //needed to visit tracking
    if (!injectSkipVisitTracker()) {
      inject(CustomerPortalVisitNotificationsService);
    }

    initStoreDispatcher(this._dataBus);
    listenToResizeAndNotifyHostWindow({
      // eslint-disable-next-line @nx/workspace-signals
      content: this.layout,
      dataBus: this._dataBus,
    });

    this._loadLookupsOnceAuthorized();
    this._unregisterNGSW();
  }

  ngOnInit(): void {
    if (ngServerMode) {
      return;
    }

    this._injector
      .get(IDLE$)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        // Preload toast notifications once the app is idle
        // so we can rely on it even when user goes offline.
        this._injector.get(HotToastLoader).preloadHotToast();
      });
  }

  private _loadLookupsOnceAuthorized(): void {
    // TODO(portal-url-migration): remove once we don't support legacy presentations URL.
    if (this._document.location.pathname.startsWith('/projects')) {
      // Should not be loaded if the user navigates to legacy `/presentations` URL,
      // since it's an extra request.
      this._store
        .select(CustomerPortalAuthQueries.getSession)
        .pipe(
          filter((v) => !!v),
          take(1),
          takeUntilDestroyed()
        )
        .subscribe(() => {
          this._store.dispatch([
            new LookupsActions.Load('Carriers'),
            new LookupsActions.Load('Countries'),
          ]);
        });
    }

    this._dataBus.dispatch(
      new DataBusStateActionMessage(
        new CustomerPortalIframeEncoreStateActions.Init()
      )
    );
  }

  private async _unregisterNGSW(): Promise<void> {
    const ngswStorage = new LocalStorageItemManager<boolean>(
      'NGSW_UNREGISTERED'
    );
    if (ngswStorage.value()) {
      return;
    }
    const w = injectWindow();
    await clearSiteData({
      serviceWorker: w.navigator?.serviceWorker,
      caches: w.caches,
    });
    ngswStorage.set(true);
  }
}
